import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus,
  FolderKanban,
  ExternalLink,
  MoreVertical,
  Trash2,
  Settings,
  Link2,
  Pencil,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { fetchProjects, createProject, deleteProject, updateProject, fetchProjectsWithStats } from '../api/projects';
import Modal from '../components/Modal';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import { MovingBorderButton } from '../components/ui/moving-border';
import { cn } from '../lib/utils';
import { PageHeader } from '../components/ui/PageHeader';
import { AnimatedCard, CardHeader, CardContent } from '../components/ui/AnimatedCard';
import { Button } from '../components/ui/Button';

function Projects() {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    name: '',
    target_domain: '',
    target_url: '',
    rank_check_schedule: 'daily',
    alert_email: '',
    alert_mode: 'instant',
  });
  const [editProject, setEditProject] = useState({
    name: '',
    target_domain: '',
    target_url: '',
    rank_check_schedule: 'daily',
    alert_email: '',
    alert_mode: 'instant',
  });
  // Form validation errors
  const [errors, setErrors] = useState({
    name: '',
    target_domain: '',
    alert_email: '',
  });
  const [editErrors, setEditErrors] = useState({
    name: '',
    target_domain: '',
    alert_email: '',
  });
  // Ref to prevent double-submission (synchronous check)
  const isSubmittingRef = useRef(false);

  // Validate a single field
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          return 'Project name is required';
        }
        if (value.trim().length < 2) {
          return 'Project name must be at least 2 characters';
        }
        return '';
      case 'target_domain':
        if (!value.trim()) {
          return 'Target domain is required';
        }
        // Simple domain validation - alphanumeric, dots, hyphens, no spaces
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
        if (!domainRegex.test(value.trim())) {
          return 'Please enter a valid domain (e.g., example.com)';
        }
        return '';
      case 'alert_email':
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return 'Please enter a valid email address';
        }
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {
      name: validateField('name', newProject.name),
      target_domain: validateField('target_domain', newProject.target_domain),
      alert_email: validateField('alert_email', newProject.alert_email),
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.target_domain && !newErrors.alert_email;
  };

  // Handle field change with live validation
  const handleFieldChange = (fieldName, value) => {
    setNewProject({ ...newProject, [fieldName]: value });
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: '' });
    }
  };

  // Handle field blur - validate on blur
  const handleFieldBlur = (fieldName) => {
    const error = validateField(fieldName, newProject[fieldName]);
    setErrors({ ...errors, [fieldName]: error });
  };

  // Track whether the form has unsaved changes (non-empty data in the modal)
  const hasUnsavedChanges = useMemo(() => {
    return isCreateModalOpen && (
      newProject.name.trim() !== '' ||
      newProject.target_domain.trim() !== '' ||
      newProject.alert_email.trim() !== ''
    );
  }, [isCreateModalOpen, newProject.name, newProject.target_domain, newProject.alert_email]);

  // Use the unsaved changes hook to warn users before leaving/refreshing
  useUnsavedChanges(hasUnsavedChanges);

  const { data, isLoading } = useQuery({
    queryKey: ['projects-with-stats'],
    queryFn: fetchProjectsWithStats,
  });

  const createMutation = useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      isSubmittingRef.current = false;
      queryClient.invalidateQueries(['projects-with-stats']);
      setIsCreateModalOpen(false);
      setNewProject({
        name: '',
        target_domain: '',
        alert_email: '',
        alert_mode: 'instant',
      });
      toast.success('Project created successfully');
    },
    onError: (error) => {
      isSubmittingRef.current = false;
      toast.error(error.response?.data?.message || 'Failed to create project');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries(['projects-with-stats']);
      toast.success('Project deleted');
    },
    onError: () => {
      toast.error('Failed to delete project');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['projects-with-stats']);
      setIsEditModalOpen(false);
      setEditingProject(null);
      setEditProject({
        name: '',
        target_domain: '',
        alert_email: '',
        alert_mode: 'instant',
      });
      toast.success('Project updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update project');
    },
  });

  const handleCreate = (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Prevent double-submission using ref (synchronous check)
    if (isSubmittingRef.current || createMutation.isPending) {
      return;
    }
    isSubmittingRef.current = true;
    // Filter out empty strings for optional fields
    const projectData = {
      name: newProject.name,
      target_domain: newProject.target_domain,
      target_url: newProject.target_url?.trim() || null,
      rank_check_schedule: newProject.rank_check_schedule,
      alert_mode: newProject.alert_mode,
    };
    if (newProject.alert_email && newProject.alert_email.trim()) {
      projectData.alert_email = newProject.alert_email;
    }
    createMutation.mutate(projectData);
  };

  const handleDelete = (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This will also delete all associated backlinks.`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setEditProject({
      name: project.name,
      target_domain: project.target_domain,
      target_url: project.target_url || '',
      rank_check_schedule: project.rank_check_schedule || 'daily',
      alert_email: project.alert_email || '',
      alert_mode: project.alert_mode || 'instant',
    });
    setEditErrors({ name: '', target_domain: '', alert_email: '' });
    setIsEditModalOpen(true);
  };

  const handleUpdate = (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {
      name: validateField('name', editProject.name),
      target_domain: validateField('target_domain', editProject.target_domain),
      alert_email: validateField('alert_email', editProject.alert_email),
    };
    setEditErrors(newErrors);

    if (newErrors.name || newErrors.target_domain || newErrors.alert_email) {
      return;
    }

    const projectData = {
      name: editProject.name,
      target_domain: editProject.target_domain,
      target_url: editProject.target_url?.trim() || null,
      rank_check_schedule: editProject.rank_check_schedule,
      alert_mode: editProject.alert_mode,
    };
    if (editProject.alert_email && editProject.alert_email.trim()) {
      projectData.alert_email = editProject.alert_email;
    }

    updateMutation.mutate({ id: editingProject.id, data: projectData });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProject(null);
    setEditProject({
      name: '',
      target_domain: '',
      target_url: '',
      rank_check_schedule: 'daily',
      alert_email: '',
      alert_mode: 'instant',
    });
    setEditErrors({ name: '', target_domain: '', alert_email: '' });
  };

  // Handle closing the modal with confirmation if there are unsaved changes
  const handleCloseModal = () => {
    if (hasUnsavedChanges) {
      const shouldClose = window.confirm('You have unsaved changes. Are you sure you want to close this form?');
      if (!shouldClose) {
        return;
      }
    }
    setIsCreateModalOpen(false);
    // Reset form and errors when closing
    setNewProject({
      name: '',
      target_domain: '',
      target_url: '',
      rank_check_schedule: 'daily',
      alert_email: '',
      alert_mode: 'instant',
    });
    setErrors({ name: '', target_domain: '', alert_email: '' });
  };

  const projects = data?.projects || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-muted rounded animate-pulse" />
          <div className="h-10 w-40 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Projects"
          description="Manage your backlink monitoring projects"
        />
        <MovingBorderButton
          onClick={() => setIsCreateModalOpen(true)}
          borderRadius="0.5rem"
        >
          <Plus className="h-5 w-5" />
          New Project
        </MovingBorderButton>
      </div>

      {/* Projects Grid */}
      {projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <AnimatedCard
              key={project.id}
              variant="interactive"
              className="relative group"
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FolderKanban className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-semibold text-foreground hover:text-primary transition-colors"
                      >
                        {project.name}
                      </Link>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" />
                        {project.target_domain}
                      </p>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  <div className="relative flex gap-1 z-20">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(project); }}
                      className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary rounded-lg transition-colors"
                      title="Edit project"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(project.id, project.name); }}
                      className="p-2 text-muted-foreground hover:text-destructive hover:bg-secondary rounded-lg transition-colors"
                      title="Delete project"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  {project.stats ? (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Links</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{project.stats.total}</span>
                          {project.stats.total > 0 && (
                            <span className="text-xs text-success">{project.stats.live} live</span>
                          )}
                        </div>
                      </div>
                      {project.stats.issues > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Issues</span>
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            project.stats.issues >= 5
                              ? 'bg-destructive/15 text-destructive'
                              : 'bg-warning/15 text-warning'
                          )}>
                            {project.stats.issues}
                          </span>
                        </div>
                      )}
                      {project.stats.avg_quality != null && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Avg Quality</span>
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-semibold',
                            project.stats.avg_quality >= 60
                              ? 'bg-success/15 text-success'
                              : project.stats.avg_quality >= 35
                                ? 'bg-warning/15 text-warning'
                                : 'bg-destructive/15 text-destructive'
                          )}>
                            {project.stats.avg_quality}
                          </span>
                        </div>
                      )}
                      {project.stats.last_checked && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Checked</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(project.stats.last_checked).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Links</span>
                        <span className="text-xs text-muted-foreground">0</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Alert Mode</span>
                        <span className="capitalize text-foreground text-xs">{project.alert_mode}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* View link overlay */}
                <Link
                  to={`/projects/${project.id}`}
                  className="absolute inset-0 z-10"
                />
              </CardContent>
            </AnimatedCard>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border rounded-xl">
          <FolderKanban className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No projects yet
          </h3>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            Create your first project to start monitoring backlinks to your website.
          </p>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Create Project
          </Button>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        title="Create Project"
      >
        <form onSubmit={handleCreate} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Project Name *
            </label>
            <input
              type="text"
              value={newProject.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              onBlur={() => handleFieldBlur('name')}
              aria-invalid={errors.name ? 'true' : 'false'}
              aria-describedby={errors.name ? 'name-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                errors.name ? 'border-destructive' : 'border-border'
              )}
              placeholder="My Website Backlinks"
            />
            {errors.name && (
              <p id="name-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target Domain *
            </label>
            <input
              type="text"
              value={newProject.target_domain}
              onChange={(e) => handleFieldChange('target_domain', e.target.value)}
              onBlur={() => handleFieldBlur('target_domain')}
              aria-invalid={errors.target_domain ? 'true' : 'false'}
              aria-describedby={errors.target_domain ? 'target-domain-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                errors.target_domain ? 'border-destructive' : 'border-border'
              )}
              placeholder="example.com"
            />
            {errors.target_domain && (
              <p id="target-domain-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{errors.target_domain}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target URL <span className="text-muted-foreground font-normal">(optional — specific page to track)</span>
            </label>
            <input
              type="text"
              value={newProject.target_url}
              onChange={(e) => setNewProject({ ...newProject, target_url: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="example.com/portugal-golden-visa"
            />
            <p className="mt-1 text-xs text-muted-foreground">If set, rankings will only match this URL path. Leave empty for domain-wide matching.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Rank Check Schedule
            </label>
            <select
              value={newProject.rank_check_schedule}
              onChange={(e) => setNewProject({ ...newProject, rank_check_schedule: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="manual">Manual Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Alert Email
            </label>
            <input
              type="email"
              value={newProject.alert_email}
              onChange={(e) => handleFieldChange('alert_email', e.target.value)}
              onBlur={() => handleFieldBlur('alert_email')}
              aria-invalid={errors.alert_email ? 'true' : 'false'}
              aria-describedby={errors.alert_email ? 'alert-email-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                errors.alert_email ? 'border-destructive' : 'border-border'
              )}
              placeholder="alerts@example.com"
            />
            {errors.alert_email && (
              <p id="alert-email-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{errors.alert_email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Alert Mode
            </label>
            <select
              value={newProject.alert_mode}
              onChange={(e) => setNewProject({ ...newProject, alert_mode: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
            >
              <option value="instant">Instant</option>
              <option value="daily">Daily Digest</option>
              <option value="weekly">Weekly Digest</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              loading={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Project"
      >
        <form onSubmit={handleUpdate} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Project Name *
            </label>
            <input
              type="text"
              value={editProject.name}
              onChange={(e) => {
                setEditProject({ ...editProject, name: e.target.value });
                if (editErrors.name) setEditErrors({ ...editErrors, name: '' });
              }}
              aria-invalid={editErrors.name ? 'true' : 'false'}
              aria-describedby={editErrors.name ? 'edit-name-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                editErrors.name ? 'border-destructive' : 'border-border'
              )}
              placeholder="My Website Backlinks"
            />
            {editErrors.name && (
              <p id="edit-name-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{editErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target Domain *
            </label>
            <input
              type="text"
              value={editProject.target_domain}
              onChange={(e) => {
                setEditProject({ ...editProject, target_domain: e.target.value });
                if (editErrors.target_domain) setEditErrors({ ...editErrors, target_domain: '' });
              }}
              aria-invalid={editErrors.target_domain ? 'true' : 'false'}
              aria-describedby={editErrors.target_domain ? 'edit-target-domain-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                editErrors.target_domain ? 'border-destructive' : 'border-border'
              )}
              placeholder="example.com"
            />
            {editErrors.target_domain && (
              <p id="edit-target-domain-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{editErrors.target_domain}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target URL <span className="text-muted-foreground font-normal">(optional — specific page to track)</span>
            </label>
            <input
              type="text"
              value={editProject.target_url}
              onChange={(e) => setEditProject({ ...editProject, target_url: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="example.com/portugal-golden-visa"
            />
            <p className="mt-1 text-xs text-muted-foreground">If set, rankings will only match this URL path. Leave empty for domain-wide matching.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Rank Check Schedule
            </label>
            <select
              value={editProject.rank_check_schedule}
              onChange={(e) => setEditProject({ ...editProject, rank_check_schedule: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="manual">Manual Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Alert Email
            </label>
            <input
              type="email"
              value={editProject.alert_email}
              onChange={(e) => {
                setEditProject({ ...editProject, alert_email: e.target.value });
                if (editErrors.alert_email) setEditErrors({ ...editErrors, alert_email: '' });
              }}
              aria-invalid={editErrors.alert_email ? 'true' : 'false'}
              aria-describedby={editErrors.alert_email ? 'edit-alert-email-error' : undefined}
              className={cn(
                'w-full px-3 py-2 bg-muted border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors',
                editErrors.alert_email ? 'border-destructive' : 'border-border'
              )}
              placeholder="alerts@example.com"
            />
            {editErrors.alert_email && (
              <p id="edit-alert-email-error" role="alert" aria-live="polite" className="mt-1 text-sm text-destructive">{editErrors.alert_email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Alert Mode
            </label>
            <select
              value={editProject.alert_mode}
              onChange={(e) => setEditProject({ ...editProject, alert_mode: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
            >
              <option value="instant">Instant</option>
              <option value="daily">Daily Digest</option>
              <option value="weekly">Weekly Digest</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseEditModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              loading={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Projects;
